import ZTable, { IColumnType, ITableRef } from '@/components/ZTable';
import { formatDisplayFullDate, ModalWidth, ROW_GUTTER, VALIDATE_REQUIRED } from '@/config';
import { Button, Col, Form, Input, InputNumber, message, Modal, Row, Select } from 'antd';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { MoneyCollectOutlined, SaveOutlined } from '@ant-design/icons';
import { AccounEventType, AccountActionType, BlobStorageContainerType, StationBalanceEntityRef, StationBalanceType } from '@/constants';
import SingleImageUpload from '@/components/FormItems/SingleImageUpload';
import { StationBalanceDto, PartnerAccountDto } from '@/__generated';
import { apiSdk } from '@/httpClient';
import { fileService } from '@/services/fileService';

type Props = {
  onClose: (reload?: boolean) => void;
  stationId: string;
};

const columns: IColumnType<StationBalanceDto>[] = [
  {
    title: 'stationsPage_StationBalanceEntityType',
    dataIndex: 'entityType',
    ellipsis: true,
    render: v => StationBalanceEntityRef.__getValue(v),
  },
  {
    title: 'stationsPage_StationBalanceEvent',
    dataIndex: 'stationEvent',
    ellipsis: true,
    render: v => AccounEventType.__getValue(v),
  },

  {
    title: 'stationsPage_StationBalanceStationAction',
    dataIndex: 'stationAction',
    ellipsis: true,
    render: v => AccountActionType.__getValue(v),
  },
  {
    title: 'stationsPage_CurrentBalance',
    dataIndex: 'currentBalance',
    ellipsis: true,
  },
  {
    title: 'stationsPage_StationBalanceAmount',
    dataIndex: 'currentBalance',
    ellipsis: true,
  },
  {
    title: 'stationsPage_StationBalanceDocuments',
    dataIndex: 'fileImageUrl',
    ellipsis: true,
    align: 'center',
    render: v =>
      v ? (
        <Button type="link" onClick={() => fileService.openProtected(v)}>
          View
        </Button>
      ) : (
        <></>
      ),
  },
  {
    title: 'base.CreatedOn',
    dataIndex: 'createdOn',
    ellipsis: true,
    width: 150,
    render: v => formatDisplayFullDate(v),
  },
];

export default function ManageBalanceModal({ onClose, stationId }: Props) {
  const { t } = useTranslation();
  const zTb = React.useRef<ITableRef>();

  const [form] = Form.useForm();
  const [loading, setLoading] = React.useState<boolean>(false);
  const [formVisible, setFormVisible] = React.useState(false);

  const updateStationBalance = (values: any) => {
    setLoading(true);
    apiSdk.StationService.updateStationBalance({ body: { ...values, stationId: stationId } })
      .then(response => {
        if (response?.success) {
          setFormVisible(false);
          onClose(true);
        } else {
          if (response.message) {
            message.error(t(response.message || ''));
          }
        }
      })
      .catch(error => {
        message.error(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };
  return (
    <Modal
      style={{ top: 10, minHeight: 500 }}
      visible={true}
      maskClosable={false}
      width={ModalWidth.large}
      className="nta-custom-form"
      title={t('stationsPage_StationBalance')}
      forceRender={true}
      cancelText={t('Close')}
      okButtonProps={{ style: { display: 'none' } }}
      onCancel={() => onClose()}
    >
      <ZTable<PartnerAccountDto>
        columns={columns}
        primaryKey="id"
        tableRef={zTb}
        defaultFilter={{
          stationId: stationId,
        }}
        url="/fleets/stations/get-station-balance-logs"
        bordered
        order_by="createdOn"
        order_by_asc={false}
        allowEdit={false}
        allowDelete={false}
        extraBtns={
          <Button
            key="create"
            type="primary"
            className="nta-create-btn"
            icon={<MoneyCollectOutlined />}
            onClick={() => {
              form.resetFields();
              setFormVisible(true);
            }}
          >
            {t('stationsPage_ManageBalance')}
          </Button>
        }
      />
      {formVisible ? (
        <Modal
          style={{ top: 10 }}
          visible={true}
          maskClosable={false}
          width={ModalWidth.small}
          className="nta-custom-form"
          title={t('stationsPage_ManageBalance')}
          forceRender={true}
          okText={t('save')}
          cancelText={t('cancel')}
          okButtonProps={{ icon: <SaveOutlined />, className: 'save-btn', loading: loading }}
          onCancel={() => {
            form.resetFields();
            setFormVisible(false);
          }}
          onOk={() => {
            form
              .validateFields()
              .then(values => {
                updateStationBalance(values);
              })
              .catch(info => {});
          }}
        >
          <Form form={form} size="middle" layout="horizontal" labelAlign="right" labelCol={{ md: 10, xs: 24 }}>
            <Row gutter={ROW_GUTTER}>
              <Col md={24}>
                <Form.Item
                  label={t('stationsPage_StationBalanceEntityType')}
                  name="stationType"
                  rules={[{ required: true, message: VALIDATE_REQUIRED }]}
                >
                  <Select options={StationBalanceType.__getSelectOptions()} />
                </Form.Item>
                <Form.Item
                  label={t('stationsPage_StationBalanceStationAction')}
                  name="stationAction"
                  rules={[{ required: true, message: VALIDATE_REQUIRED }]}
                >
                  <Select options={AccountActionType.__getSelectOptions()} />
                </Form.Item>
                <Form.Item label={t('stationsPage_StationBalanceAmount')} name="amount" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                  <InputNumber min={0} className="w-100" />
                </Form.Item>
                <Form.Item label={t('base.Description')} name="description" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                  <Input.TextArea />
                </Form.Item>
                <Form.Item label={t('stationsPage_StationBalanceDocuments')} name="imageUrl" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                  <SingleImageUpload blobStorageContainer={BlobStorageContainerType.StationBalanceProofs} />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Modal>
      ) : null}
    </Modal>
  );
}
